import { FC, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchActiveEmployees,
  fetchAllEmployees,
  updateActiveEmployee,
} from "../../store/allocation/employeeSlice";
import {
  splitEmployeeNames,
} from "../../utils/employeeNames";

type Props = {
  selectedDesigner: string;
  selectedCaptain: string;
  onSelect: (role: "Designer" | "Captain", empName: string) => void;
  isAll?: boolean;
};

type ConfirmState = {
  type: "add" | "remove" | null;
  empName?: string;
};

const ActiveEmployees: FC<Props> = ({
  selectedDesigner,
  selectedCaptain,
  onSelect,
  isAll = false,
}) => {
  const dispatch = useAppDispatch();
  const { allEmployees, activeEmployees } = useAppSelector(
    (state) => state.employee,
  );

  const [showAddList, setShowAddList] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>({ type: null });

  useEffect(() => {
    dispatch(fetchAllEmployees());
    dispatch(fetchActiveEmployees());
  }, [dispatch]);

  /* ---------- FILTERS ---------- */

  const eligibleEmployees = allEmployees.filter(
    (emp) =>
      emp.verified &&
      (emp.role === "Designer" || emp.role === "Senior Designer" || emp.role === "Captain" || emp.role === "Assistant Captain" || emp.role === "Super Admin") &&
      !emp.active,
  );

  const sourceEmployees = isAll
    ? allEmployees.filter(
      (emp) => emp.role === "Captain" || emp.role === "Assistant Captain" || emp.role === "Senior Designer" || emp.role === "Designer" || emp.role === "Super Admin",
    )
    : activeEmployees;

  const captains = sourceEmployees.filter((emp) => emp.role === "Captain" || emp.role === "Assistant Captain" || emp.role === "Senior Designer" || emp.role === "Super Admin");

  const designers = sourceEmployees.filter((emp) => emp.role === "Designer" || emp.role === "Senior Designer" || emp.role === "Captain" || emp.role === "Assistant Captain" || emp.role === "Super Admin");

  const selectedDesignerNames = splitEmployeeNames(selectedDesigner);

  /* ---------- HANDLERS ---------- */

  const handleAddToggle = () => {
    setShowAddList((prev) => !prev);
  };

  const requestAdd = (empName: string) => {
    setConfirm({ type: "add", empName });
  };

  const requestRemove = () => {
    if (selectedCaptain) {
      setConfirm({ type: "remove", empName: selectedCaptain });
    } else if (selectedDesigner) {
      setConfirm({ type: "remove", empName: selectedDesigner });
    }
  };

  const confirmAction = () => {
    if (!confirm.type || !confirm.empName) return;

    dispatch(
      updateActiveEmployee({
        emp_name: confirm.empName,
        action: confirm.type === "add" ? "present" : "absent",
      }),
    );

    setConfirm({ type: null });
    setShowAddList(false);
  };

  /* ---------- UI ---------- */
  return (
    <>
      <div className="flex text-xs gap-2 items-start">
        {/* Action Buttons */}
        {!isAll && (
          <div className="flex gap-2 border-2 p-2 items-center rounded-xl w-48">
            <button
              onClick={handleAddToggle}
              className="px-3 py-1 bg-green-500 text-white rounded-md"
            >
              Add +
            </button>

            <button
              onClick={requestRemove}
              disabled={!selectedCaptain && !selectedDesigner}
              className="px-3 py-1 bg-red-500 text-white rounded-md disabled:opacity-50"
            >
              Remove -
            </button>
          </div>
        )}

        {/* Active Employees */}
        <div className="flex gap-3 p-2 w-full h-full items-center">
          {/* Captains */}
          <div>
            {captains.length > 0 && (
              <div className="flex gap-3 flex-wrap">
                {captains.map((emp) => (
                  <button
                    key={emp.emp_name}
                    onClick={() =>
                      onSelect(
                        "Captain",
                        selectedCaptain === emp.emp_name ? "" : emp.emp_name,
                      )
                    }
                    className={`px-2 py-1 rounded-xl border-2 ${selectedCaptain === emp.emp_name
                      ? "bg-violet-600 text-white border-violet-700"
                      : "bg-violet-400 text-white border-violet-600 hover:bg-violet-500"
                      }`}
                  >
                    {emp.emp_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-[2px] h-full bg-gray-400"></div>

          <div>
            {/* Designers */}
            {designers.length > 0 && (
              <div className="flex gap-3 flex-wrap">
                {designers.map((emp) => {
                  const isSelected = selectedDesignerNames.includes(
                    emp.emp_name,
                  );
                  return (
                  <button
                    key={emp.emp_name}
                    onClick={() =>
                      onSelect(
                        "Designer",
                        emp.emp_name,
                      )
                    }
                    className={`px-2 py-1 rounded-xl border-2 ${isSelected
                      ? "bg-blue-600 text-white border-blue-700"
                      : "bg-blue-400 text-white border-blue-600 hover:bg-blue-500"
                      }`}
                  >
                    {emp.emp_name}
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Employee List */}
      {showAddList && (
        <div className="mt-3 p-3 border-2 rounded-md flex flex-wrap gap-3">
          {eligibleEmployees.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No eligible employees available
            </p>
          ) : (
            eligibleEmployees.map((emp) => (
              <button
                key={emp.emp_name}
                onClick={() => requestAdd(emp.emp_name)}
                className="px-3 py-1 rounded-xl border-2 bg-gray-100 hover:bg-blue-500 hover:text-white"
              >
                {emp.emp_name}
              </button>
            ))
          )}
        </div>
      )}

      {/* Confirm Modal */}
      {confirm.type && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-5 w-[320px]">
            <h3 className="text-lg font-semibold mb-3">
              Confirm {confirm.type === "add" ? "Add" : "Remove"}
            </h3>

            <p className="mb-4">
              Are you sure you want to{" "}
              {confirm.type === "add" ? "add" : "remove"}{" "}
              <b>{confirm.empName}</b>?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirm({ type: null })}
                className="px-3 py-1 border rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                className="px-3 py-1 bg-blue-600 text-white rounded-md"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActiveEmployees;
